import React, { useCallback, useMemo } from 'react';
import { Chip, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { SpecificationViewModel } from '@api/enterprise/openapi/models/specification-view-model';
import { TestAccordionSection } from '../TestAccordionSection';
import { BasicReadingTest } from '../BasicReadingTest';
import { getStatusBadgeProps } from '../shared';
import { getStatusConfig } from '../../utils';
import type { BasicReadingTestData } from '../BasicReadingTest';
import type { OverallTestStatus } from '../../types';

const toOverallStatus = (
  status?: BasicReadingTestData['status']
): OverallTestStatus => {
  switch (status) {
    case 'Pass':
      return 'pass';
    case 'Fail':
      return 'fail';
    case 'InProgress':
      return 'in-progress';
    default:
      return 'not-started';
  }
};

type Props = {
  testData?: BasicReadingTestData;
  specifications?: SpecificationViewModel[];
  onSave?: (data: BasicReadingTestData) => void;
  isReadOnly?: boolean;
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
};

const BasicReadingTestSection: React.FC<Props> = ({
  testData,
  specifications,
  onSave,
  isReadOnly = false,
  expanded,
  onExpandedChange
}) => {
  const { t } = useTranslation('calibration');
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const overallStatus = toOverallStatus(testData?.status);

  const statusConfig = useMemo(
    () => getStatusConfig(isDarkMode)[overallStatus],
    [isDarkMode, overallStatus]
  );

  const badgeProps = useMemo(
    () => getStatusBadgeProps(overallStatus, isDarkMode),
    [isDarkMode, overallStatus]
  );

  const handleExpandedChange = useCallback(
    (isExpanded: boolean) => {
      onExpandedChange(isExpanded);
    },
    [onExpandedChange]
  );

  const statusIndicator = (
    <Chip label={badgeProps.label} size="small" sx={badgeProps.sx} />
  );

  return (
    <TestAccordionSection
      id="basic-reading-test"
      title={t('basicReading.sectionTitle')}
      status={statusConfig}
      expanded={expanded}
      onExpandedChange={handleExpandedChange}
      isDarkMode={isDarkMode}
      statusIndicator={statusIndicator}
    >
      <BasicReadingTest
        testData={testData}
        specifications={specifications}
        onSave={onSave}
        isReadOnly={isReadOnly}
      />
    </TestAccordionSection>
  );
};

export default BasicReadingTestSection;
